import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardBody, CardHeader } from "../../../../_metronic/_partials/controls";
import { SubHeader } from "../../Common/layout/SubHeader";
import ReactApexChart from "react-apexcharts";
import { useGetGemsExchangeRateActiveQuery, useGetGemsOverviewQuery } from "../../../../redux/apiSlice";
import { formatStatus } from "../../../util/Helpers";

const todayStr = new Date().toISOString().split("T")[0];

const formatMoney = (currency, value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "—";
    return `${currency || "NGN"} ${numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const getScrollableChartWidth = (points, { minWidth = 640, pxPerPoint = 110 } = {}) => {
    const count = Number(points || 0);
    if (!Number.isFinite(count) || count <= 0) return minWidth;
    return Math.max(minWidth, count * pxPerPoint);
};

const getTimeSeriesTimestamp = (point) => {
    const candidates = [point?.periodStart, point?.periodKey, point?.date, point?.created, point?.periodLabel];

    for (const candidate of candidates) {
        if (!candidate) continue;
        const timestamp = new Date(candidate).getTime();
        if (Number.isFinite(timestamp)) return timestamp;
    }

    return 0;
};

const AxisGuide = ({ maxValue, label }) => {
    const max = Number(maxValue || 0);
    const safeMax = Number.isFinite(max) && max > 0 ? max : 0;
    const mid = safeMax / 2;
    return (
        <div className="d-flex flex-column justify-content-between pr-3" style={{ width: 84, height: 240 }}>
            <div className="text-muted font-size-sm">{label}</div>
            <div className="d-flex flex-column justify-content-between flex-grow-1 mt-2">
                <div className="text-muted font-size-sm text-right">{safeMax.toLocaleString()}</div>
                <div className="text-muted font-size-sm text-right">{mid ? mid.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "0"}</div>
                <div className="text-muted font-size-sm text-right">0</div>
            </div>
        </div>
    );
};

const GemsRedemptions = () => {
    const { userGroup } = useParams();

    const [filters, setFilters] = useState({
        startDate: "",
        endDate: todayStr,
    });
    const [conversionGems, setConversionGems] = useState("");

    const overviewArgs = useMemo(
        () => ({
            userGroup,
            startDate: filters.startDate || "",
            endDate: filters.endDate || "",
            period: "",
            rewardTypes: [],
        }),
        [userGroup, filters.startDate, filters.endDate]
    );

    const { data: overviewResponse, isFetching: isFetchingOverview } = useGetGemsOverviewQuery(overviewArgs);
    const { data: activeExchangeRate, isFetching: isFetchingActiveExchangeRate } = useGetGemsExchangeRateActiveQuery(
        { userGroup }
    );

    const activeRate = activeExchangeRate?.data || activeExchangeRate || null;
    const activeRateValue = Number(activeRate?.rate ?? 0);
    const activeRateCurrency = activeRate?.currency || "NGN";
    const gemsToCash = (gems) => {
        const numericGems = Number(gems);
        if (!Number.isFinite(numericGems) || !Number.isFinite(activeRateValue) || activeRateValue <= 0) return null;
        return numericGems * activeRateValue;
    };
    const conversionCashValue = gemsToCash(conversionGems);

    const overview = useMemo(() => {
        const payload = overviewResponse?.data ?? overviewResponse ?? null;
        return payload && typeof payload === "object" ? payload : {};
    }, [overviewResponse]);
    const totalRedeemed = Number(overview?.totalRedeemed ?? 0);
    const totalEarned = Number(overview?.totalEarned ?? 0);
    const netGems = totalEarned - totalRedeemed;
    const timeSeries = useMemo(
        () =>
            (Array.isArray(overview?.timeSeries) ? overview.timeSeries : [])
                .slice()
                .sort((a, b) => getTimeSeriesTimestamp(b) - getTimeSeriesTimestamp(a)),
        [overview?.timeSeries]
    );
    const [timeSeriesPage, setTimeSeriesPage] = useState(1);
    const [timeSeriesPageSize, setTimeSeriesPageSize] = useState(7);
    const timeSeriesTotalPages = useMemo(
        () => Math.max(1, Math.ceil(timeSeries.length / timeSeriesPageSize)),
        [timeSeries.length, timeSeriesPageSize]
    );
    useEffect(() => {
        setTimeSeriesPage(1);
    }, [filters.startDate, filters.endDate, timeSeriesPageSize, userGroup]);
    useEffect(() => {
        if (timeSeriesPage > timeSeriesTotalPages) setTimeSeriesPage(timeSeriesTotalPages);
    }, [timeSeriesPage, timeSeriesTotalPages]);
    const pagedTimeSeries = useMemo(() => {
        const start = (Math.max(1, timeSeriesPage) - 1) * timeSeriesPageSize;
        return timeSeries.slice(start, start + timeSeriesPageSize);
    }, [timeSeries, timeSeriesPage, timeSeriesPageSize]);
    const timeSeriesLabels = useMemo(() => pagedTimeSeries.map((p) => p.periodLabel), [pagedTimeSeries]);
    const timeSeriesRedeemed = useMemo(() => pagedTimeSeries.map((p) => Number(p.totalRedeemed ?? 0)), [pagedTimeSeries]);
    const hasTimeSeriesChartData = timeSeriesRedeemed.some((v) => v > 0);
    const timeSeriesChartWidth = useMemo(() => getScrollableChartWidth(timeSeriesLabels.length), [timeSeriesLabels.length]);

    const onFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    return (
        <>
            {userGroup && (
                <SubHeader>
                    <SubHeader.Nav>
                        <SubHeader.NavItem label={"Overview"} to={`/gems/${userGroup}/overview`} />
                        <SubHeader.NavItem label={"Accruals"} to={`/gems/${userGroup}/overview/accruals`} />
                        <SubHeader.NavItem label={"Redemptions"} to={`/gems/${userGroup}/overview/redemptions`} />
                        <SubHeader.NavItem label={"Campaigns"} to={`/gems/${userGroup}/overview/campaigns`} />
                        <SubHeader.NavItem label={"Reward attempts"} to={`/gems/${userGroup}/overview/reward-attempts`} />
                        <SubHeader.NavItem label={"Audit logs"} to={`/gems/${userGroup}/overview/audit-logs`} />
                    </SubHeader.Nav>
                </SubHeader>
            )}
            <div className="py-4 px-6">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="font-weight-bolder mb-1">Gems redemptions</h2>
                        <span className="text-muted">Redemption totals and trend for the selected period.</span>
                        <div className="d-flex flex-wrap mt-4">
                            <div className="mr-3 mb-2">
                                <label className="text-muted font-size-sm mb-1">Start date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="startDate"
                                    value={filters.startDate}
                                    onChange={onFilterChange}
                                />
                            </div>
                            <div className="mb-2">
                                <label className="text-muted font-size-sm mb-1">End date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="endDate"
                                    value={filters.endDate}
                                    onChange={onFilterChange}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="text-right" style={{ marginRight: "4rem" }}>
                        {isFetchingActiveExchangeRate ? (
                            <div className="text-muted">Loading rate...</div>
                        ) : activeRate ? (
                            <div className="d-flex flex-column align-items-end">
                                <span className="text-muted font-size-sm">Active exchange rate</span>
                                <span className="font-weight-bolder">
                                    1 Gem = {Number(activeRate.rate ?? 0).toLocaleString()} {activeRate.currency || "—"}
                                </span>
                                <span className="text-muted font-size-sm">{formatStatus(activeRate.status || "active")}</span>
                                <div className="d-flex align-items-center mt-3">
                                    <input
                                        type="number"
                                        className="form-control form-control-sm mr-2"
                                        value={conversionGems}
                                        onChange={(e) => setConversionGems(e.target.value)}
                                        placeholder="Gems"
                                        style={{ width: 110 }}
                                        min="0"
                                    />
                                    <span className="text-muted font-size-sm">
                                        {conversionGems
                                            ? conversionCashValue !== null
                                                ? `≈ ${formatMoney(activeRateCurrency, conversionCashValue)}`
                                                : "—"
                                            : `Convert to ${activeRateCurrency}`}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="text-muted">No active exchange rate</div>
                        )}
                    </div>
                </div>

                <div className="row mb-6">
                    <div className="col-md-4 mb-4">
                        <Card>
                            <CardBody>
                                <div className="d-flex flex-column">
                                    <span className="text-muted font-size-sm mb-2">Total redeemed</span>
                                    <span className="font-weight-bolder font-size-h3 text-danger">
                                        {totalRedeemed.toLocaleString()}
                                    </span>
                                    <span className="text-muted font-size-sm">
                                        {gemsToCash(totalRedeemed) !== null ? `≈ ${formatMoney(activeRateCurrency, gemsToCash(totalRedeemed))}` : "—"}
                                    </span>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                    <div className="col-md-4 mb-4">
                        <Card>
                            <CardBody>
                                <div className="d-flex flex-column">
                                    <span className="text-muted font-size-sm mb-2">Net Gems</span>
                                    <span className="font-weight-bolder font-size-h3 text-primary">
                                        {netGems.toLocaleString()}
                                    </span>
                                    <span className="text-muted font-size-sm">
                                        {gemsToCash(netGems) !== null ? `≈ ${formatMoney(activeRateCurrency, gemsToCash(netGems))}` : "—"}
                                    </span>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                    <div className="col-md-4 mb-4">
                        <Card>
                            <CardBody>
                                <div className="d-flex flex-column">
                                    <span className="text-muted font-size-sm mb-2">Periods</span>
                                    <span className="font-weight-bolder font-size-h3">
                                        {timeSeries.length.toLocaleString()}
                                    </span>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>

                <div className="row mb-6">
                    <div className="col-12">
                        <Card>
                            <CardHeader
                                title="Redeemed over time"
                                toolbar={
                                    <div className="d-flex align-items-center">
                                        <select
                                            className="form-control form-control-sm mr-3"
                                            value={timeSeriesPageSize}
                                            onChange={(e) => setTimeSeriesPageSize(Number(e.target.value))}
                                            style={{ width: 120 }}
                                        >
                                            <option value={7}>7 / page</option>
                                            <option value={14}>14 / page</option>
                                            <option value={30}>30 / page</option>
                                        </select>
                                        <div className="text-muted font-size-sm mr-3">
                                            Page {timeSeriesPage} of {timeSeriesTotalPages}
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-light-primary btn-sm mr-2"
                                            onClick={() => setTimeSeriesPage((p) => Math.max(1, p - 1))}
                                            disabled={timeSeriesPage <= 1}
                                        >
                                            Prev
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-light-primary btn-sm"
                                            onClick={() => setTimeSeriesPage((p) => Math.min(timeSeriesTotalPages, p + 1))}
                                            disabled={timeSeriesPage >= timeSeriesTotalPages}
                                        >
                                            Next
                                        </button>
                                    </div>
                                }
                            />
                            <CardBody>
                                <div className="text-muted mb-4">
                                    Redeemed Gems per period. Value uses the active exchange rate.
                                </div>
                                {isFetchingOverview ? (
                                    <div className="text-center text-muted py-5">Loading...</div>
                                ) : hasTimeSeriesChartData ? (
                                    <div className="d-flex">
                                        <AxisGuide maxValue={Math.max(...timeSeriesRedeemed, 0)} label="Gems" />
                                        <div style={{ overflowX: "auto", overflowY: "hidden", flex: 1 }}>
                                            <div style={{ minWidth: timeSeriesChartWidth }}>
                                                <ReactApexChart
                                                    options={{
                                                        chart: { toolbar: { show: false } },
                                                        dataLabels: { enabled: false },
                                                        xaxis: {
                                                            categories: timeSeriesLabels,
                                                            labels: {
                                                                rotate: -45,
                                                                rotateAlways: false,
                                                                hideOverlappingLabels: false,
                                                                trim: false,
                                                            },
                                                        },
                                                        yaxis: { show: false },
                                                        plotOptions: { bar: { columnWidth: "55%" } },
                                                        colors: ["#F64E60"],
                                                    }}
                                                    series={[{ name: "Redeemed", data: timeSeriesRedeemed }]}
                                                    type="bar"
                                                    height={240}
                                                    width={timeSeriesChartWidth}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-5">No redemption data</div>
                                )}

                                <div className="table-responsive mt-6">
                                    <table className="table table-sm table-head-custom">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th className="text-right">Redeemed</th>
                                                <th className="text-right">Value</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isFetchingOverview ? (
                                                <tr>
                                                    <td colSpan={3} className="text-center text-muted py-5">
                                                        Loading...
                                                    </td>
                                                </tr>
                                            ) : pagedTimeSeries.length ? (
                                                pagedTimeSeries.map((item) => {
                                                    const redeemed = Number(item.totalRedeemed ?? 0);
                                                    const cashValue = gemsToCash(redeemed);
                                                    return (
                                                        <tr key={item.periodKey || item.periodLabel}>
                                                            <td>{item.periodLabel}</td>
                                                            <td className="text-right text-danger">{redeemed.toLocaleString()}</td>
                                                            <td className="text-right text-muted">
                                                                {cashValue !== null ? formatMoney(activeRateCurrency, cashValue) : "—"}
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={3} className="text-center text-muted py-5">
                                                        No redemption data for selected period
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </div>
        </>
    );
};

export default GemsRedemptions;
